import { formatPeriodYoyValue, resolveTrendChartPeriodValue } from '../../../filters/lib/statisticsPeriodYoyHelpers';
import { resolveTrendChartPalette, resolveTrendYearSeriesColor, resolvePeriodComparePointColor } from '../../../common/lib/statisticsChartPalette';
import { formatPrimaryPhaseMetricValue, formatPrimaryPhasePercentValue, isPrimaryPhasePercentMetric } from '../../pipeline/lib/orderPrimaryPhaseMetric';
import {
	isHorizontalBarOrientation,
	resolveBarBorderRadius,
	resolveBarLabelPosition,
	CHART_BAR_ORIENTATION,
} from '../../shared/lib/barOrientation';
import {
	isTrendBarChart,
	TREND_CHART_TYPE,
	TREND_LINE_SHAPE,
	resolveCompareLineSmooth,
} from './trendChartConfig';
import {
	buildDimensionCategoryAxisLayout,
	buildPeriodCompareCategoryAxisLayout,
	resolveHorizontalCompareBarSeriesStyle,
	resolveHorizontalBarGapSeriesProps,
	resolveVerticalCompareBarGapSeriesProps,
	resolveYearMonthsCompareBoundaryGap,
	resolveHorizontalCompareBarGridBottom,
} from './trendChartAxisLayout';
import {
	buildCompareAxisPointer,
	buildCompareValueYAxis,
	buildCompareLineLegend,
	buildLinePointLabel,
	buildPeriodCompareYearLegendSeries,
	resolveTrendChartGridTop,
	resolvePeriodCompareCategoryLabel,
	resolvePeriodCompareLegendName,
	resolvePeriodCompareTooltipHeader,
} from './trendChartOptionShared';

const resolveCompareMetricAxisProps = (chartMetric, { currencyMetric = false, formatAxisValue } = {}) => {
	if (isPrimaryPhasePercentMetric(chartMetric)) {
		return {
			currencyMetric: false,
			allowFraction: true,
			formatAxisValue: formatAxisValue ?? formatPrimaryPhasePercentValue,
		};
	}
	return {
		currencyMetric,
		formatAxisValue,
	};
};

export const buildPeriodCompareLineOption = ({
	timeline,
	chartMetric,
	metricLabel,
	currencyMetric,
	chartTheme,
	accentColor,
	showPointLabels = true,
	chartType = TREND_CHART_TYPE.LINE,
	lineShape = TREND_LINE_SHAPE.SMOOTH,
	barOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	isLight = false,
	yearSeriesColors = {},
}) => {
	const { comparePoint: comparePointColor } = resolveTrendChartPalette(isLight);
	const categories = timeline.map((point) => resolvePeriodCompareCategoryLabel(point) || point.axisLabel);
	const values = timeline.map((point) => resolveTrendChartPeriodValue(point.startDate, point.value));
	const anchorIndex = timeline.findIndex((point) => point.kind === 'anchor');
	const isBar = isTrendBarChart(chartType);
	const horizontalBar = isBar && isHorizontalBarOrientation(barOrientation);
	const lineSmooth = resolveCompareLineSmooth(lineShape);
	const valueAxis = buildCompareValueYAxis(chartTheme, {
		...resolveCompareMetricAxisProps(chartMetric, { currencyMetric }),
		values,
	});
	const barBorderRadius = resolveBarBorderRadius(horizontalBar);
	const barLabelPosition = resolveBarLabelPosition(horizontalBar);
	const categoryAxisLayout = !horizontalBar && isBar
		? buildPeriodCompareCategoryAxisLayout(categories, chartTheme, { anchorIndex, accentColor })
		: null;

	let compareColorIndex = 0;
	const resolvePointColor = (point) => {
		const isAnchor = point?.kind === 'anchor';
		const compareIndex = isAnchor ? 0 : compareColorIndex++;
		return resolvePeriodComparePointColor(point, {
			yearSeriesColors,
			accentColor,
			isLight,
			fallbackCompareColor: comparePointColor,
			compareIndex,
		});
	};
	const pointColors = timeline.map((point) => resolvePointColor(point));
	const useYearLegend = timeline.length >= 2;
	const yearLegendNames = useYearLegend ? timeline.map((point) => resolvePeriodCompareLegendName(point)) : null;
	const legend = yearLegendNames
		? {
				...buildCompareLineLegend(chartTheme, { data: yearLegendNames, itemCount: yearLegendNames.length }),
				selectedMode: false,
			}
		: undefined;
	const yearLegendSeries = useYearLegend ? buildPeriodCompareYearLegendSeries(timeline, pointColors) : [];
	const pointData = values.map((value, index) => ({
		value,
		itemStyle: {
			color: pointColors[index],
			borderRadius: barBorderRadius,
		},
		label: { color: pointColors[index] },
	}));

	if (horizontalBar) {
		const barStyle = resolveHorizontalCompareBarSeriesStyle(1);
		return {
			backgroundColor: 'transparent',
			animationDurationUpdate: 0,
			...(legend ? { legend } : {}),
			grid: {
				left: 8,
				right: 24,
				top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: yearLegendNames?.length ?? 1 }),
				bottom: resolveHorizontalCompareBarGridBottom(),
				containLabel: true,
			},
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: buildCompareAxisPointer(chartTheme, chartType),
				formatter: (items) => {
					const item = items?.find((entry) => entry.seriesName === metricLabel) ?? items?.[0];
					if (!item) return '';
					const point = timeline[item.dataIndex];
					if (!point) return '';
					const valueText = formatPrimaryPhaseMetricValue(item.value, chartMetric);
					const lines = [resolvePeriodCompareTooltipHeader(point)];
					if (point.periodLabel && point.periodLabel !== point.axisLabel && point.kind !== 'anchor') {
						lines.push(point.periodLabel);
					}
					lines.push(`${metricLabel} ${valueText}`);
					return lines.join('<br/>');
				},
			},
			xAxis: valueAxis,
			yAxis: {
				type: 'category',
				data: categories,
				inverse: true,
				...chartTheme.axis,
				splitLine: { show: false },
				axisLabel: {
					...chartTheme.axis.axisLabel,
					color: chartTheme.tooltip?.textStyle?.color ?? chartTheme.axis.axisLabel?.color,
					fontSize: 11,
					fontWeight: 600,
				},
			},
			series: [
				...yearLegendSeries,
				{
					name: metricLabel,
					type: 'bar',
					barMaxWidth: barStyle.barMaxWidth,
					barGap: barStyle.barGap,
					barCategoryGap: barStyle.barCategoryGap,
					data: pointData,
					label: buildLinePointLabel({
						show: showPointLabels,
						chartTheme,
						color: null,
						formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
						position: barLabelPosition,
					}),
					emphasis: { focus: 'series' },
					animationDuration: 0,
					animationDurationUpdate: 0,
					animationEasing: 'cubicOut',
				},
			],
		};
	}

	return {
		backgroundColor: 'transparent',
		animationDurationUpdate: 0,
		...(legend ? { legend } : {}),
		grid: {
			left: 6,
			right: 6,
			top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: yearLegendNames?.length ?? 1 }),
			bottom: categoryAxisLayout?.gridBottom ?? (anchorIndex >= 0 ? 12 : 4),
			containLabel: true,
		},
		...(categoryAxisLayout?.dataZoom?.length ? { dataZoom: categoryAxisLayout.dataZoom } : {}),
		tooltip: {
			...chartTheme.tooltip,
			trigger: 'axis',
			axisPointer: buildCompareAxisPointer(chartTheme, chartType),
			formatter: (items) => {
				const item = items?.find((entry) => entry.seriesName === metricLabel) ?? items?.[0];
				if (!item) return '';
				const point = timeline[item.dataIndex];
				if (!point) return '';
				if (item.value == null) return resolvePeriodCompareTooltipHeader(point);
				const valueText = formatPrimaryPhaseMetricValue(item.value, chartMetric);
				const lines = [resolvePeriodCompareTooltipHeader(point)];
				if (point.periodLabel && point.periodLabel !== point.axisLabel && point.kind !== 'anchor') {
					lines.push(point.periodLabel);
				}
				lines.push(`${metricLabel} ${valueText}`);
				return lines.join('<br/>');
			},
		},
		xAxis: {
			type: 'category',
			data: categories,
			boundaryGap: true,
			...chartTheme.axis,
			axisLabel: categoryAxisLayout?.axisLabel ?? {
				...chartTheme.axis.axisLabel,
				color: chartTheme.tooltip?.textStyle?.color ?? chartTheme.axis.axisLabel?.color,
				fontSize: 11,
				fontWeight: 600,
				interval: 0,
				hideOverlap: false,
				showMinLabel: true,
				showMaxLabel: true,
				margin: 10,
				overflow: 'none',
				formatter: (value, index) => {
					const point = timeline[index];
					if (point?.kind === 'anchor') return `{anchor|${value}}\n{tag|基准}`;
					return value;
				},
				rich: {
					anchor: { fontWeight: 700, fontSize: 11, color: accentColor, lineHeight: 16 },
					tag: { fontSize: 9, color: accentColor, lineHeight: 13 },
				},
			},
			axisTick: { alignWithLabel: true },
			splitLine: { show: false },
		},
		yAxis: buildCompareValueYAxis(chartTheme, {
			...resolveCompareMetricAxisProps(chartMetric, { currencyMetric }),
			values,
		}),
		series: [
			...yearLegendSeries,
			isBar
				? {
						name: metricLabel,
						type: 'bar',
						barMaxWidth: 40,
						...resolveVerticalCompareBarGapSeriesProps(0),
						data: pointData,
						label: buildLinePointLabel({
							show: showPointLabels,
							chartTheme,
							color: null,
							formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
						}),
						emphasis: { focus: 'series' },
						animationDuration: 0,
						animationDurationUpdate: 0,
						animationEasing: 'cubicOut',
					}
				: {
						name: metricLabel,
						type: 'line',
						smooth: lineSmooth,
						connectNulls: false,
						data: values.map((value, index) => ({
							value,
							symbolSize: timeline[index]?.kind === 'anchor' ? 9 : 6,
							itemStyle: {
								color: pointColors[index],
								borderColor: timeline[index]?.kind === 'anchor' ? '#fff' : pointColors[index],
								borderWidth: timeline[index]?.kind === 'anchor' ? 2 : 1,
							},
							label: { color: pointColors[index] },
						})),
						lineStyle: { width: 2.5, color: accentColor },
						areaStyle: {
							color: {
								type: 'linear',
								x: 0,
								y: 0,
								x2: 0,
								y2: 1,
								colorStops: [
									{ offset: 0, color: `${accentColor}33` },
									{ offset: 1, color: `${accentColor}05` },
								],
							},
						},
						label: buildLinePointLabel({
							show: showPointLabels,
							chartTheme,
							color: null,
							formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
						}),
						emphasis: { focus: 'series', scale: true },
						animationDuration: 0,
						animationDurationUpdate: 0,
						animationEasing: 'cubicOut',
						markPoint:
							anchorIndex >= 0
								? {
										symbol: 'pin',
										symbolSize: 36,
										symbolOffset: [0, '-35%'],
										data: [{ name: '基准', coord: [anchorIndex, values[anchorIndex]], value: '基准' }],
										label: { fontSize: 8, color: '#fff', fontWeight: 700 },
										itemStyle: { color: accentColor },
									}
								: undefined,
					},
		],
	};
};

export const buildYearMonthsCompareLineOption = ({
	yearMonthsSeries,
	valueField,
	granularity,
	chartTheme,
	currencyMetric,
	accentColor,
	showPointLabels = true,
	chartType = TREND_CHART_TYPE.LINE,
	lineShape = TREND_LINE_SHAPE.SMOOTH,
	barOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	isLight = false,
	yearSeriesColors = {},
}) => {
	if (!yearMonthsSeries?.series?.length) return null;

	const { axisLabels, series, formatAxisValue, anchorYear, percentMetric = false } = yearMonthsSeries;
	const sortedSeries = [...series].sort((left, right) => {
		if (left.year === anchorYear) return -1;
		if (right.year === anchorYear) return 1;
		return left.year - right.year;
	});
	let compareColorIndex = 0;
	const resolveYearColor = (year, isAnchor) => {
		const fallbackIndex = isAnchor ? 0 : compareColorIndex++;
		return resolveTrendYearSeriesColor({
			year,
			isAnchor,
			colors: yearSeriesColors,
			accentColor,
			isLight,
			fallbackIndex,
		});
	};
	const isBar = isTrendBarChart(chartType);
	const horizontalBar = isBar && isHorizontalBarOrientation(barOrientation);
	const lineSmooth = resolveCompareLineSmooth(lineShape);
	const barBorderRadius = resolveBarBorderRadius(horizontalBar);
	const barLabelPosition = resolveBarLabelPosition(horizontalBar);
	const valueAxis = buildCompareValueYAxis(chartTheme, {
		currencyMetric: percentMetric ? false : currencyMetric,
		allowFraction: percentMetric || currencyMetric,
		formatAxisValue,
		values: sortedSeries.map((item) => item.values),
	});

	if (horizontalBar) {
		const barStyle = resolveHorizontalCompareBarSeriesStyle(sortedSeries.length);
		return {
			backgroundColor: 'transparent',
			animationDurationUpdate: 0,
			grid: {
				left: 8,
				right: 24,
				top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: sortedSeries.length }),
				bottom: resolveHorizontalCompareBarGridBottom(),
				containLabel: true,
			},
			legend: buildCompareLineLegend(chartTheme, { itemCount: sortedSeries.length }),
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: buildCompareAxisPointer(chartTheme, chartType),
				formatter: (items) => {
					if (!items?.length) return '';
					const lines = [items[0].name];
					for (const item of items) {
						lines.push(`${item.marker}${item.seriesName}：${formatPeriodYoyValue(item.value, valueField)}`);
					}
					return lines.join('<br/>');
				},
			},
			xAxis: valueAxis,
			yAxis: {
				type: 'category',
				data: axisLabels,
				inverse: true,
				...chartTheme.axis,
				splitLine: { show: false },
				axisLabel: { ...chartTheme.axis.axisLabel, fontSize: 9, interval: 0 },
			},
			series: sortedSeries.map((item, index) => {
				const isAnchor = item.year === anchorYear;
				const lineColor = resolveYearColor(item.year, isAnchor);
				return {
					name: isAnchor ? `${item.year}年（基准）` : `${item.year}年`,
					type: 'bar',
					data: item.values,
					z: isAnchor ? 3 : 2,
					barMaxWidth: barStyle.barMaxWidth,
					...(resolveHorizontalBarGapSeriesProps(
						sortedSeries.map((item) => ({ yAxisIndex: 0, year: item.year })),
						index,
						barStyle,
					) ?? null),
					itemStyle: { color: lineColor, borderRadius: barBorderRadius },
					label: buildLinePointLabel({
						show: showPointLabels,
						chartTheme,
						color: lineColor,
						formatValue: (value) => formatPeriodYoyValue(value, valueField),
						position: barLabelPosition,
					}),
					emphasis: { focus: 'series' },
				};
			}),
		};
	}

	return {
		backgroundColor: 'transparent',
		animationDurationUpdate: 0,
		grid: {
			left: 8,
			right: 8,
			top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: sortedSeries.length }),
			bottom: 4,
			containLabel: true,
		},
		legend: buildCompareLineLegend(chartTheme, { itemCount: sortedSeries.length }),
		tooltip: {
			...chartTheme.tooltip,
			trigger: 'axis',
			axisPointer: buildCompareAxisPointer(chartTheme, chartType),
			formatter: (items) => {
				if (!items?.length) return '';
				const lines = [items[0].axisValue];
				for (const item of items) {
					if (item.value == null) continue;
					lines.push(`${item.marker}${item.seriesName}：${formatPeriodYoyValue(item.value, valueField)}`);
				}
				return lines.join('<br/>');
			},
		},
		xAxis: {
			type: 'category',
			data: axisLabels,
			boundaryGap: resolveYearMonthsCompareBoundaryGap({ isBar, horizontalBar, granularity }),
			...chartTheme.axis,
			axisLabel: { ...chartTheme.axis.axisLabel, fontSize: 9, interval: 0 },
			...(isBar ? { axisTick: { alignWithLabel: true }, splitLine: { show: false } } : {}),
		},
		yAxis: buildCompareValueYAxis(chartTheme, {
			currencyMetric: percentMetric ? false : currencyMetric,
			allowFraction: percentMetric || currencyMetric,
			formatAxisValue,
			values: sortedSeries.map((item) => item.values),
		}),
		series: sortedSeries.map((item, index) => {
			const isAnchor = item.year === anchorYear;
			const lineColor = resolveYearColor(item.year, isAnchor);
			if (isBar) {
				return {
					name: isAnchor ? `${item.year}年（基准）` : `${item.year}年`,
					type: 'bar',
					data: item.values,
					z: isAnchor ? 3 : 2,
					barMaxWidth: 22,
					...resolveVerticalCompareBarGapSeriesProps(index),
					itemStyle: { color: lineColor, borderRadius: [3, 3, 0, 0] },
					label: buildLinePointLabel({
						show: showPointLabels,
						chartTheme,
						color: lineColor,
						formatValue: (value) => formatPeriodYoyValue(value, valueField),
					}),
					emphasis: { focus: 'series' },
				};
			}
			return {
				name: isAnchor ? `${item.year}年（基准）` : `${item.year}年`,
				type: 'line',
				smooth: lineSmooth,
				connectNulls: false,
				data: item.values,
				z: isAnchor ? 3 : 2,
				symbolSize: isAnchor ? 7 : 5,
				lineStyle: {
					width: isAnchor ? 2.5 : 2,
					color: lineColor,
					type: isAnchor ? 'solid' : 'dashed',
				},
				itemStyle: {
					color: lineColor,
					borderColor: isAnchor ? '#fff' : lineColor,
					borderWidth: isAnchor ? 1.5 : 0,
				},
				label: buildLinePointLabel({
					show: showPointLabels,
					chartTheme,
					color: lineColor,
					formatValue: (value) => formatPeriodYoyValue(value, valueField),
				}),
				emphasis: { focus: 'series', scale: isAnchor },
			};
		}),
	};
};

export const buildDimensionAxisSnapshotLineOption = ({
	axisLabels = [],
	values = [],
	chartMetric,
	metricLabel,
	fieldLabel = '',
	currencyMetric,
	chartTheme,
	accentColor,
	showPointLabels = true,
	dimensionAxisMode = '',
	chartType = TREND_CHART_TYPE.LINE,
	lineShape = TREND_LINE_SHAPE.SMOOTH,
	barOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	isLight = false,
}) => {
	if (!axisLabels.length || !values.length) return null;

	const { anchor: defaultAnchor } = resolveTrendChartPalette(isLight);
	const isBar = isTrendBarChart(chartType);
	const horizontalBar = isBar && isHorizontalBarOrientation(barOrientation);
	const categoryAxis = buildDimensionCategoryAxisLayout(axisLabels, dimensionAxisMode, chartTheme, {
		categoryOnY: horizontalBar,
	});
	const lineSmooth = resolveCompareLineSmooth(lineShape);
	const barColor = accentColor || defaultAnchor;
	const barBorderRadius = resolveBarBorderRadius(horizontalBar);
	const barLabelPosition = resolveBarLabelPosition(horizontalBar);
	const valueAxis = buildCompareValueYAxis(chartTheme, {
		...resolveCompareMetricAxisProps(chartMetric, { currencyMetric }),
		values,
	});
	const barStyle = horizontalBar ? resolveHorizontalCompareBarSeriesStyle(1) : null;
	const categoryAxisConfig = {
		type: 'category',
		data: axisLabels,
		boundaryGap: true,
		...(horizontalBar ? { inverse: true } : {}),
		...chartTheme.axis,
		splitLine: { show: false },
		axisLabel: {
			...chartTheme.axis.axisLabel,
			...categoryAxis.axisLabel,
		},
	};

	return {
		backgroundColor: 'transparent',
		animationDurationUpdate: 0,
		grid: {
			left: categoryAxis.gridLeft ?? 8,
			right: 8,
			top: showPointLabels ? 28 : 18,
			bottom: categoryAxis.gridBottom,
			containLabel: true,
		},
		...(categoryAxis.dataZoom.length ? { dataZoom: categoryAxis.dataZoom } : {}),
		tooltip: {
			...chartTheme.tooltip,
			trigger: 'axis',
			axisPointer: buildCompareAxisPointer(chartTheme, chartType),
			formatter: (items) => {
				const item = items?.[0];
				if (!item) return '';
				return `${item.name ?? item.axisValue}<br/>${metricLabel} ${formatPrimaryPhaseMetricValue(item.value, chartMetric)}`;
			},
		},
		xAxis: horizontalBar ? valueAxis : categoryAxisConfig,
		yAxis: horizontalBar ? categoryAxisConfig : valueAxis,
		series: [
			isBar
				? {
						name: fieldLabel || metricLabel,
						type: 'bar',
						barMaxWidth: barStyle?.barMaxWidth ?? 36,
						barGap: barStyle?.barGap,
						barCategoryGap: barStyle?.barCategoryGap,
						...(!horizontalBar ? resolveVerticalCompareBarGapSeriesProps(0) : null),
						data: values,
						itemStyle: { color: barColor, borderRadius: barBorderRadius },
						label: buildLinePointLabel({
							show: showPointLabels,
							chartTheme,
							color: chartTheme.labelColor,
							formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
							position: barLabelPosition,
						}),
						emphasis: { focus: 'series' },
					}
				: {
						name: fieldLabel || metricLabel,
						type: 'line',
						smooth: lineSmooth,
						data: values,
						lineStyle: { width: 2.5, color: barColor },
						itemStyle: { color: barColor },
						label: buildLinePointLabel({
							show: showPointLabels,
							chartTheme,
							color: chartTheme.labelColor,
							formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
						}),
						emphasis: { focus: 'series', scale: true },
					},
		],
	};
};

export const buildDimensionCompareLineOption = ({
	dimensionSeries,
	chartMetric,
	metricLabel,
	currencyMetric,
	chartTheme,
	showPointLabels = true,
	dimensionAxisMode = '',
	chartType = TREND_CHART_TYPE.LINE,
	lineShape = TREND_LINE_SHAPE.SMOOTH,
	barOrientation = CHART_BAR_ORIENTATION.VERTICAL,
	isLight = false,
}) => {
	if (!dimensionSeries?.series?.length) return null;

	const { yearLines: yearLineColors } = resolveTrendChartPalette(isLight);
	const { axisLabels, series } = dimensionSeries;
	const isBar = isTrendBarChart(chartType);
	const horizontalBar = isBar && isHorizontalBarOrientation(barOrientation);
	const categoryAxis = buildDimensionCategoryAxisLayout(axisLabels, dimensionAxisMode, chartTheme, {
		categoryOnY: horizontalBar,
	});
	let colorIndex = 0;
	const lineSmooth = resolveCompareLineSmooth(lineShape);
	const barBorderRadius = resolveBarBorderRadius(horizontalBar);
	const barLabelPosition = resolveBarLabelPosition(horizontalBar);
	const valueAxis = buildCompareValueYAxis(chartTheme, {
		...resolveCompareMetricAxisProps(chartMetric, { currencyMetric }),
		values: series,
	});
	const barStyle = horizontalBar ? resolveHorizontalCompareBarSeriesStyle(series.length) : null;
	const compareSeriesDefs = series.map((item) => ({ yAxisIndex: 0, label: item.label }));
	const categoryAxisConfig = {
		type: 'category',
		data: axisLabels,
		boundaryGap: true,
		...(horizontalBar ? { inverse: true } : {}),
		...chartTheme.axis,
		splitLine: { show: false },
		axisLabel: { ...chartTheme.axis.axisLabel, ...categoryAxis.axisLabel },
	};

	return {
		backgroundColor: 'transparent',
		animationDurationUpdate: 0,
		grid: {
			left: categoryAxis.gridLeft ?? 8,
			right: 8,
			top: resolveTrendChartGridTop({ showPointLabels, legendItemCount: series.length }),
			bottom: categoryAxis.gridBottom,
			containLabel: true,
		},
		...(categoryAxis.dataZoom.length ? { dataZoom: categoryAxis.dataZoom } : {}),
		legend: buildCompareLineLegend(chartTheme, { itemCount: series.length }),
		tooltip: {
			...chartTheme.tooltip,
			trigger: 'axis',
			axisPointer: buildCompareAxisPointer(chartTheme, chartType),
			formatter: (items) => {
				if (!items?.length) return '';
				const lines = [items[0].name ?? items[0].axisValue];
				for (const item of items) {
					lines.push(`${item.marker}${item.seriesName}：${formatPrimaryPhaseMetricValue(item.value, chartMetric)}`);
				}
				return lines.join('<br/>');
			},
		},
		xAxis: horizontalBar ? valueAxis : categoryAxisConfig,
		yAxis: horizontalBar ? categoryAxisConfig : valueAxis,
		series: series.map((item, index) => {
			const lineColor = yearLineColors[colorIndex++ % yearLineColors.length];
			if (isBar) {
				return {
					name: item.label,
					type: 'bar',
					data: item.values,
					barMaxWidth: barStyle?.barMaxWidth ?? 22,
					...(horizontalBar && barStyle ? resolveHorizontalBarGapSeriesProps(compareSeriesDefs, index, barStyle) : null),
					...(!horizontalBar ? resolveVerticalCompareBarGapSeriesProps(index) : null),
					itemStyle: { color: lineColor, borderRadius: barBorderRadius },
					label: buildLinePointLabel({
						show: showPointLabels,
						chartTheme,
						color: lineColor,
						formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
						position: barLabelPosition,
					}),
					emphasis: { focus: 'series' },
				};
			}
			return {
				name: item.label,
				type: 'line',
				smooth: lineSmooth,
				data: item.values,
				lineStyle: { width: 2.5, color: lineColor },
				itemStyle: { color: lineColor },
				label: buildLinePointLabel({
					show: showPointLabels,
					chartTheme,
					color: lineColor,
					formatValue: (value) => formatPrimaryPhaseMetricValue(value, chartMetric),
				}),
				emphasis: { focus: 'series', scale: true },
			};
		}),
	};
};
